'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { PoolShareRow } from '@/components/market/pool-share-row';
import { AnimatedD6 } from '@/components/market/position-card';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Skeleton } from '@/components/ui/app-skeleton';
import { CardSectionLabel } from '@/components/ui/card-section-label';
import { GiftIconSolid } from '@/components/ui/icon/glyphs';
import { formatPercentTrim } from '@/lib/chart/format';
import { useFormattedCurrency } from '@/lib/currency';
import { cn } from '@/lib/ui/utils';
export type BackerRewardsState = 'loading' | 'claimable' | 'backer-unresolved' | 'backer-claimed' | 'non-backer';
export interface BackerRewardsView {
  /** Discriminant — exactly one state per render. */
  state: BackerRewardsState;
  /** Headline amount in display units (D6 decoded) — live vault while
   *  shares are unclaimed, LIFETIME pool total after a full claim. Never
   *  shrinks to 0 because a claim drained the vault (MOR-1835). */
  headlineAmount: number;
  /** User's pro-rata earnings in display units — used for claim eligibility. */
  earnedAmount: number;
  /** Exact D6 representation of the user's pro-rata earnings. */
  earnedD6: string;
  /** Fraction of the pot, 0..1 — shown before the market resolves. */
  userSharePct: number;
  /** True while a claim tx is in flight — disables + spinners the button. */
  isClaiming: boolean;
  /** Fire the claim — invoked only from the claimable button. */
  onClaim: () => void;
}
export interface BackerRewardsCardViewProps extends BackerRewardsView {
  className?: string | undefined;
}

/**
 * Backer Rewards card — presentational leaf. Renders one of five states
 * (loading / claimable / backer-not-resolved / claimed / non-backer) from a plain
 * view-model, with no data wiring of its own. The connected
 * `BackerRewardsCard` supplies the props from `useBackerRewards`; the workspace
 * preview supplies fixtures.
 *
 * Mirrors the Prize Pool card's header+value idiom (gift icon + uppercase label
 * + large currency value) so the two stacked cards read as one design family.
 */
export function BackerRewardsCardView(t0) {
  const $ = _c(41);
  const {
    state,
    headlineAmount,
    earnedAmount,
    earnedD6,
    userSharePct,
    isClaiming,
    onClaim,
    className
  } = t0;
  const t = useTranslations("market.backerRewards");
  const {
    formatBalance,
    formatD6,
    locale
  } = useFormattedCurrency();
  let t1;
  let t2;
  if ($[0] !== className || $[1] !== earnedAmount || $[2] !== earnedD6 || $[3] !== formatBalance || $[4] !== formatD6 || $[5] !== headlineAmount || $[6] !== isClaiming || $[7] !== locale || $[8] !== onClaim || $[9] !== state || $[10] !== t || $[11] !== userSharePct) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const earnedDisplay = formatD6(earnedD6);
      const claimDisabled = isClaiming || earnedAmount <= 0;
      if (state === "loading") {
        let t3;
        if ($[14] !== className) {
          t3 = cn("flex w-full max-w-sm flex-col gap-3 rounded-card border bg-background-primary p-4", className);
          $[14] = className;
          $[15] = t3;
        } else {
          t3 = $[15];
        }
        let t4;
        if ($[16] !== t) {
          t4 = t("header");
          $[16] = t;
          $[17] = t4;
        } else {
          t4 = $[17];
        }
        let t5;
        if ($[18] !== t4) {
          t5 = <CardSectionLabel icon={GiftIconSolid}>{t4}</CardSectionLabel>;
          $[18] = t4;
          $[19] = t5;
        } else {
          t5 = $[19];
        }
        let t6;
        let t7;
        let t8;
        if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <Skeleton className="h-10 w-2/3" />;
          t7 = <Skeleton className="h-10 w-full rounded-panel-inset" />;
          t8 = <Skeleton className="h-12 w-full rounded-button" />;
          $[20] = t6;
          $[21] = t7;
          $[22] = t8;
        } else {
          t6 = $[20];
          t7 = $[21];
          t8 = $[22];
        }
        let t9;
        if ($[23] !== t3 || $[24] !== t5) {
          t9 = <div className={t3} data-testid="backer-rewards-viewer-loading">{t5}{t6}{t7}{t8}</div>;
          $[23] = t3;
          $[24] = t5;
          $[25] = t9;
        } else {
          t9 = $[25];
        }
        t2 = t9;
        break bb0;
      }
      let t3;
      if ($[26] !== className) {
        t3 = cn("flex w-full max-w-sm flex-col gap-3 rounded-card border bg-background-primary p-4", className);
        $[26] = className;
        $[27] = t3;
      } else {
        t3 = $[27];
      }
      let t4;
      if ($[28] !== t) {
        t4 = t("header");
        $[28] = t;
        $[29] = t4;
      } else {
        t4 = $[29];
      }
      let t5;
      if ($[30] !== t4) {
        t5 = <CardSectionLabel icon={GiftIconSolid}>{t4}</CardSectionLabel>;
        $[30] = t4;
        $[31] = t5;
      } else {
        t5 = $[31];
      }
      let t6;
      if ($[32] !== formatBalance || $[33] !== headlineAmount) {
        t6 = <AnimatedNumber value={headlineAmount} format={formatBalance} animateOnMount={false} className="text-display-sm font-bold tracking-tight tabular-nums text-foreground-primary" />;
        $[32] = formatBalance;
        $[33] = headlineAmount;
        $[34] = t6;
      } else {
        t6 = $[34];
      }
      let t7;
      if ($[35] !== earnedD6 || $[36] !== locale || $[37] !== state || $[38] !== t || $[39] !== userSharePct) {
        t7 = state === "non-backer" ? <p className="text-caption text-foreground-secondary">{t("nonBackerCta")}</p> : <PoolShareRow label={t("yourShareLabel")} amount={<AnimatedD6 d6={earnedD6} />} sharePercent={formatPercentTrim(userSharePct, 2, locale)} />;
        $[35] = earnedD6;
        $[36] = locale;
        $[37] = state;
        $[38] = t;
        $[39] = userSharePct;
        $[40] = t7;
      } else {
        t7 = $[40];
      }
      t1 = <div className={t3}>{t5}{t6}{t7}{state === "claimable" ? <AnimatedButton variant="primary" size="lg" className="w-full" onClick={onClaim} disabled={claimDisabled} loading={isClaiming}>{isClaiming ? t("claimInFlight") : t("claimAmount", {
            amount: earnedDisplay
          })}</AnimatedButton> : state === "backer-claimed" ? <AnimatedButton variant="primary" size="lg" className="w-full" disabled={true}>{t("claimed")}</AnimatedButton> : null}</div>;
    }
    $[0] = className;
    $[1] = earnedAmount;
    $[2] = earnedD6;
    $[3] = formatBalance;
    $[4] = formatD6;
    $[5] = headlineAmount;
    $[6] = isClaiming;
    $[7] = locale;
    $[8] = onClaim;
    $[9] = state;
    $[10] = t;
    $[11] = userSharePct;
    $[12] = t1;
    $[13] = t2;
  } else {
    t1 = $[12];
    t2 = $[13];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  return t1;
}